/*!
 * gara-qms.js — Garage + Quick Maintenance Sheet (QMS).
 *
 * Built 2026-06-03. Powers sections/gara-garage-qms.liquid (the /pages/my-garage
 * page) AND the "Your Garage" block on the customer account page.
 *
 * WHAT IT DOES
 *   1. Renders a Year -> Make -> Model bike picker that reuses the SAME bike
 *      list the parts finder uses (window.GARA_BIKE_REGISTRY.bikes, falling back
 *      to window.GARA_TIRE_FINDER.bikes). Selection is canonicalized to the same
 *      slug the product index + spine use.
 *   2. Looks up that bike's spine entry in window.GARA_VEHICLE_SPECS and renders
 *      a Quick Maintenance Sheet: oil, tires, torques, battery, spark plug,
 *      fork, starter. Missing datums say "we don't have this spec yet" instead
 *      of showing a blank.
 *   3. Optionally persists a garage (list of bikes) so a rider can save more than
 *      one. Storage backend is pluggable:
 *        - localStorage (always available, the Phase-1 default), and
 *        - a metafield-backed store via an app-proxy POST when the page marks
 *          itself logged-in (data-gara-garage-backend="metafield"). Until the
 *          lead wires that endpoint, the localStorage garage is authoritative
 *          and the metafield-seeded bikes (rendered server-side) are merged in
 *          read-only.
 *
 * DATA CONTRACTS
 *   window.GARA_VEHICLE_SPECS — { "<make-slug>/<model-slug>": { ...spine... } }
 *     spine datum shapes (all optional, render-guarded):
 *       oil:    { visc:{v}, cap:{v,u,by_year?}, cap_nf:{v,u,by_year?}, jaso:{v},
 *                 filter:{pn,sku,title}, washer:{size_mm,bolt_thread,seal_type} }
 *       tire:   { f:{v,by_year}, r:{v,by_year} }
 *       fork:   { tube_dia_mm:{v,u,by_year}, seal_dim:{v,u,by_year}, make:{v,by_year} }
 *       battery:{ model, v, spec:{voltage_v,capacity_ah,cca,length_mm,width_mm,
 *                 height_mm,chemistry}, by_year? }
 *       spark:  { plug, gap, by_year:[{v,ys,ye}] }
 *       torque: { f_axle, r_axle, fork_pinch, tc_upper, tc_lower }  // plain Nm
 *       start_type: "electric" | "kickstart" | "both"
 *     Year-specific values live in a `by_year` array of {v, ys, ye}; ye may be
 *     null (open-ended). resolveByYear() returns the NARROWEST entry whose span
 *     contains the selected year, else null (strict: no nearest-year tolerance).
 *     The top-level `v` is the answer only when the datum has no by_year array
 *     to consult, or when no year was supplied at all (see resolveByYear).
 *
 *   localStorage.gara_garage — JSON [{slug, make, model, year}] (saved bikes).
 *   localStorage.gara_ymm    — shared with GaraYMM so picking a bike here also
 *                              filters every collection grid site-wide.
 */
(function (global) {
  'use strict';

  // ─── slug (MUST match canonical/index_builder._slug + gara-ymm-filter slugify) ──
  function slugify(s) {
    if (s == null) return '';
    return String(s).toLowerCase().trim()
      .replace(/\s+/g, '-')
      .replace(/\//g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Shopify page-handle sanitization (matches build_fitment_page_index.handleize) so the
  // handle we build equals the LIVE page handle even for names with +, ., parentheses, etc.
  function handleize(s) {
    return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }
  // The set of per-bike fitment pages that actually exist (gara-fitment-pages.js, regenerated
  // by build_fitment_page_index.py on every rebuild). ONE page per MODEL (parts-for-<make>-<model>);
  // the page itself is generation-aware and focuses the rider's year via ?year= (see fitmentHandle).
  var _FIT = null;
  function fitSet() {
    if (_FIT) return _FIT;
    _FIT = new Set((global.GARA_FITMENT_PAGES) || []);
    return _FIT;
  }
  // Canonical spine key for a bike. Prefers an explicit `slug` field if the
  // registry ever carries one (the lead is re-keying bikes to add it); else
  // derives make-slug/model-slug.
  function bikeSlug(bike) {
    if (bike && bike.slug) return String(bike.slug);
    return slugify(bike && bike.make) + '/' + slugify(bike && bike.model);
  }

  function registry() {
    var R = global.GARA_BIKE_REGISTRY;
    if (R && R.bikes && R.bikes.length) return R.bikes;
    var T = global.GARA_TIRE_FINDER;
    if (T && T.bikes && T.bikes.length) return T.bikes;
    return [];
  }

  // ─── year-aware value resolution ─────────────────────────────────────────
  // THE ONE shared resolver, STRICT since the 2026-07-28 generation-boundary RCA.
  // CONTRACT, exactly what the code below does:
  //   * truthy year AND a non-empty by_year array: the NARROWEST span COVERING the
  //     year wins; a tie goes to the FIRST such entry in array order; entries whose
  //     `v` is null/undefined are skipped; a missing `ys` reads as 0 and a null `ye`
  //     reads as 9999 (open-ended). Nothing covering the year returns null, an
  //     honest "we don't have this spec yet", never a guessed value. There is NO
  //     nearest-span tolerance in this file any more.
  //   * otherwise (no by_year, an empty by_year, or NO YEAR / year 0): the top-level
  //     `v` when it is non-null, else null. Read that literally: a bike that reaches
  //     renderQMS without a year takes this branch EVEN IF the datum has by_year,
  //     because the guard below is `y && ...`. That is pre-existing behaviour this
  //     patch does not change; a year-less garage entry gets the year-agnostic `v`.
  // WHY STRICT: the old +/-3 year fallback was meant to recover an edge year inside
  // the SAME generation, but it never checked the generation, so it bridged real
  // generation boundaries. Measured on the shipped asset, 3,968 bike-years across
  // 411 bikes were being answered from the WRONG generation, e.g. a 2021 BMW G 310 R
  // (gen 2021-2026) served the 2016-2020 generation's YTX9-BS battery, and a 2018
  // Aprilia RSV4 RR (gen 2018-2021) served the 2009-2017 generation's YT12A-BS.
  // WHERE EDGE-YEAR RECOVERY LIVES NOW: build side, once, generation-clamped, in
  // canonical/gen_gate.py :: extend_segments(segments, gens, tol=3). The by_year
  // spans that reach this asset are already widened as far as the generation allows,
  // so every recovered year is visible in the asset diff, and the spine file itself
  // is never touched (projection only).
  // PARITY BY CONSTRUCTION (the G6 invariant): the build-side resolver
  // canonical/tire_assets.py :: _size_at_year(datum, year, gens) IS
  //     resolve_strict(extend_segments(datum.by_year, gens), year)
  // i.e. the SAME extension this asset carries fed to the SAME strict rule this
  // function implements. One function, not twins, so the tire finder and the spec
  // sheet cannot drift apart on a (bike, year). This function on its own guarantees
  // NOTHING about generations: it is only strict. The generation clamp is entirely
  // upstream, and a year outside every gen row fails closed and is reported in
  // research/fitment/registry/_gen_gate_report.json.
  function resolveByYear(datum, year) {
    if (datum == null) return null;
    if (typeof datum !== 'object') return datum;           // primitive
    var y = Number(year) || 0;
    var by = datum.by_year;
    if (y && Array.isArray(by) && by.length) {
      var best = null, bestSpan = null;
      for (var i = 0; i < by.length; i++) {
        var e = by[i];
        if (!e || e.v == null) continue;
        var ys = Number(e.ys) || 0;
        var ye = (e.ye == null) ? 9999 : Number(e.ye);
        if (y >= ys && y <= ye) {
          var span = ye - ys;
          if (best === null || span < bestSpan) { best = e.v; bestSpan = span; }
        }
      }
      if (best !== null) return best;
      return null;                                          // no researched span covers this year
    }
    return (datum.v != null) ? datum.v : null;
  }

  function specFor(slug) {
    var DB = global.GARA_VEHICLE_SPECS;
    if (!DB || typeof DB !== 'object') return null;
    return DB[slug] || null;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ─── QMS row builders ────────────────────────────────────────────────────
  // Each "section" is {title, icon, rows:[{label, value, sub?}], link?}.
  // value === null renders the graceful "we don't have this spec yet" line.

  var MISSING = '<span class="qms-missing">We don\'t have this spec yet.</span>';

  // Capacity stays in LITERS: almost every bike except Harley is liter-spec'd by the manufacturer,
  // and moto oil is sold in 1L bottles, so liters is the natural unit (Benjamin 2026-06-11, reversing
  // the brief quarts experiment). Torque shows US-first in ft-lb with the OEM Nm in parens.
  function nmToFtlb(nm) { return Math.round(nm * 0.737562); }        // 1 Nm = 0.737562 ft-lb
  function fmtNm(n) { return (n == null) ? null : (nmToFtlb(n) + ' ft-lb (' + n + ' Nm)'); }
  // Filter brand from the PN prefix (HF=Hiflofiltro, KN=K&N, else the OE part number).
  function filterBrand(pn) {
    var p = String(pn || '').toUpperCase();
    if (p.indexOf('HF') === 0) return 'Hiflofiltro';
    if (p.indexOf('KN') === 0) return 'K&amp;N';
    return 'OE';
  }
  function fmtCap(capDatum, year) {
    var v = resolveByYear(capDatum, year);
    if (v == null) return null;
    var u = (capDatum && capDatum.u) ? capDatum.u : 'L';
    return v + ' ' + u;
  }

  function buildSections(spec, ymm, links) {
    var year = ymm.year;
    var sections = [];

    // ── Oil ──
    var oil = spec && spec.oil;
    var oilRows = [];
    if (oil) {
      var visc = resolveByYear(oil.visc, year);            // gen-aware (R6: 10W-30 -> 20W-40 -> 10W-40)
      var premixRatio = oil.premix ? resolveByYear(oil.premix, year) : null;
      var gVisc = (oil.gearbox && oil.gearbox.visc) ? resolveByYear(oil.gearbox.visc, year) : null;
      var gCap = (oil.gearbox && oil.gearbox.cap) ? fmtCap(oil.gearbox.cap, year) : null;
      var is2Stroke = (oil.stroke === '2T') || (!!oil.premix && !visc);
      // Engine type as a VISIBLE row (was logic-only until 2026-07-09 — Benjamin: show
      // every spec we hold). stroke labels come from the classification campaign + the
      // manuals crusade: '2T' / '4T' / 'electric'.
      var strokeLabel = oil.stroke === '2T' ? '2-stroke' : oil.stroke === '4T' ? '4-stroke'
        : oil.stroke === 'electric' ? 'Electric' : null;
      oilRows.push({ label: 'Engine type', value: strokeLabel });
      if (is2Stroke) {
        // 2-stroke: premix fuel/oil; separate transmission (gearbox) oil. Same row completeness
        // as the 4-stroke block (oil spec/cert + capacity are tracked rows, "we don't have this
        // spec yet" until researched), so the section never looks half-built.
        var premixSpec = oil.premix_spec ? resolveByYear(oil.premix_spec, year) : null;
        var gSpec = (oil.gearbox && oil.gearbox.spec) ? resolveByYear(oil.gearbox.spec, year) : null;
        oilRows.push({ label: 'Engine oil', value: 'Pre-mix' });
        oilRows.push({ label: 'Pre-mix ratio', value: premixRatio ? esc(premixRatio) : null });
        oilRows.push({ label: 'Pre-mix oil spec', value: premixSpec ? esc(premixSpec) : null });
        oilRows.push({ label: 'Transmission oil', value: gVisc ? esc(gVisc) : null });
        oilRows.push({ label: 'Transmission oil capacity', value: gCap });
        oilRows.push({ label: 'Transmission oil spec', value: gSpec ? esc(gSpec) : null });
        // 2T service interval = the GEAR OIL change (premix engines have no engine oil to
        // change); manuals give it in operating hours. Benjamin QA'd this gap 2026-07-10 —
        // the value was in the spine (KTM 250 EXC: 45 hr) but this branch never rendered it.
        var iHr2 = oil.interval_hr ? resolveByYear(oil.interval_hr, year) : null;
        var iMi2 = oil.interval ? resolveByYear(oil.interval, year) : null;
        oilRows.push({ label: 'Gear oil change interval', value: iHr2 ? (esc(iHr2) + ' hr')
          : iMi2 ? (esc(iMi2) + ' mi') : null });
      } else {
        // 4-stroke: complete tracked oil block. Every row is always present (null -> "we don't
        // have this spec yet") so the section is uniform and one research pass fills it all.
        var withFilter = fmtCap(oil.cap, year);
        var noFilter = fmtCap(oil.cap_nf, year);
        var interval = oil.interval ? resolveByYear(oil.interval, year) : null;
        var intervalHr = oil.interval_hr ? resolveByYear(oil.interval_hr, year) : null;
        var fpn = oil.filter ? resolveByYear(oil.filter, year) : null;       // per-gen filter PN
        var fpnAlt = oil.filter_alt ? resolveByYear(oil.filter_alt, year) : null;  // alt-brand cross-ref (K&N)
        var washer = oil.washer ? resolveByYear(oil.washer, year) : null;    // crush washer size
        var thread = oil.thread ? resolveByYear(oil.thread, year) : null;    // drain-plug thread (logic)
        var dTorque = oil.drain_torque ? resolveByYear(oil.drain_torque, year) : null;
        oilRows.push({ label: 'Oil type', value: visc || null });
        var jaso = oil.jaso ? resolveByYear(oil.jaso, year) : null;   // by_year-aware (gen-gated JASO)
        oilRows.push({ label: 'Oil standard', value: jaso ? esc(jaso) : null });
        oilRows.push({ label: 'Capacity (with filter change)', value: withFilter });
        oilRows.push({ label: 'Capacity (no filter change)', value: noFilter });
        // HOURS FIRST (Benjamin 2026-07-09: dirt bikes use hours — that's the unit
        // riders track). A bike with a researched hour interval is a dirt/competition
        // engine and shows hours even if an old miles figure exists; street bikes
        // only ever have miles. Never converted either direction.
        oilRows.push({ label: 'Change interval', value: intervalHr ? (esc(intervalHr) + ' hr')
          : interval ? (esc(interval) + ' mi') : null });
        // Two known filters (primary + filter_alt cross-ref) -> a brand-labeled dropdown so the
        // rider picks the brand they want; one known -> the plain PN (alt-only gets its brand label).
        var filterVal = null;
        if (fpn && fpnAlt && String(fpnAlt) !== String(fpn)) {
          filterVal = '<select class="qms-filter-pick" aria-label="Oil filter brand">'
            + '<option value="' + esc(fpn) + '">' + filterBrand(fpn) + ' ' + esc(fpn) + '</option>'
            + '<option value="' + esc(fpnAlt) + '">' + filterBrand(fpnAlt) + ' ' + esc(fpnAlt) + '</option>'
            + '</select>';
        } else if (fpn || fpnAlt) {
          var onlyPn = fpn || fpnAlt;
          filterVal = esc(onlyPn) + ((!fpn && fpnAlt) ? ' (' + filterBrand(onlyPn) + ')' : '');
        }
        oilRows.push({ label: 'Oil filter', value: filterVal });
        oilRows.push({ label: 'Drain plug washer', value: washer
          ? (esc(washer) + (thread ? ' (' + esc(thread) + ')' : '')) : null });
        if (thread && !washer) {  // thread known but washer not researched yet — still show it
          oilRows.push({ label: 'Drain plug thread', value: esc(thread) });
        }
        oilRows.push({ label: 'Drain plug torque', value: fmtNm(dTorque) });
        if (gVisc || gCap) {   // 4T with a separate transmission oil (e.g. some big v-twins)
          oilRows.push({ label: 'Transmission oil', value: gVisc ? esc(gVisc) : null });
          oilRows.push({ label: 'Transmission oil capacity', value: gCap });
        }
      }
    } else {
      oilRows.push({ label: 'Oil type', value: null });
      oilRows.push({ label: 'Capacity (with filter change)', value: null });
      oilRows.push({ label: 'Capacity (no filter change)', value: null });
    }
    sections.push({ key: 'oil', title: 'Oil change', icon: oilIcon(), rows: oilRows,
      link: links.oil, linkText: 'Shop oil & filters for this bike' });

    // ── Coolant ── (captured on the same service visit; section appears once researched)
    var coolant = spec && spec.coolant;
    if (coolant && (coolant.cap || coolant.spec)) {
      var coolRows = [
        { label: 'Coolant capacity', value: coolant.cap ? fmtCap(coolant.cap, year) : null },
        { label: 'Coolant type', value: (coolant.spec && coolant.spec.v)
            ? esc(resolveByYear(coolant.spec, year)) : null }
      ];
      sections.push({ key: 'coolant', title: 'Coolant', icon: oilIcon(), rows: coolRows,
        link: links.coolant, linkText: 'Shop coolant for this bike' });
    }

    // ── Tires ──
    var tire = spec && spec.tire;
    var tireRows = [{
      label: 'Front tire',
      value: tire && tire.f ? esc(resolveByYear(tire.f, year)) : null
    }, {
      label: 'Front tire rating',
      value: tire && tire.f_rating ? esc(resolveByYear(tire.f_rating, year)) : null
    }, {
      label: 'Rear tire',
      value: tire && tire.r ? esc(resolveByYear(tire.r, year)) : null
    }, {
      label: 'Rear tire rating',
      value: tire && tire.r_rating ? esc(resolveByYear(tire.r_rating, year)) : null
    }];
    // Cold tire pressures (manual-printed solo figures) — rows appear once researched.
    var tpF = (tire && tire.f_psi) ? resolveByYear(tire.f_psi, year) : null;
    var tpR = (tire && tire.r_psi) ? resolveByYear(tire.r_psi, year) : null;
    if (tpF != null || tpR != null) {
      tireRows.push({ label: 'Front tire pressure', value: tpF != null ? (tpF + ' psi (cold)') : null });
      tireRows.push({ label: 'Rear tire pressure', value: tpR != null ? (tpR + ' psi (cold)') : null });
    }
    sections.push({ key: 'tire', title: 'Tire fitment', icon: tireIcon(), rows: tireRows,
      link: links.tire, linkText: 'Find tires for this bike' });

    // ── Torque ──
    var tq = spec && spec.torque;
    // torque entries are full datums ({v, by_year?}) since S102c — resolve per selected year
    var tqRows = [
      { label: 'Front axle nut', value: tq ? fmtNm(resolveByYear(tq.f_axle, year)) : null },
      { label: 'Rear axle nut', value: tq ? fmtNm(resolveByYear(tq.r_axle, year)) : null },
      { label: 'Fork pinch bolts', value: tq ? fmtNm(resolveByYear(tq.fork_pinch, year)) : null },
      { label: 'Triple clamp (upper)', value: tq ? fmtNm(resolveByYear(tq.tc_upper, year)) : null },
      { label: 'Triple clamp (lower)', value: tq ? fmtNm(resolveByYear(tq.tc_lower, year)) : null }
    ];
    sections.push({ key: 'torque', title: 'Torque specs', icon: torqueIcon(), rows: tqRows });

    // ── Chain & sprockets ── (always shown; missing rows say "we don't have this spec yet")
    // drivetrain entries are full datums ({v, by_year?}) since the OEM-gearing absorb —
    // resolve per selected year, same pattern as torque (S102c).
    var dt = spec && spec.drivetrain;
    var dtPitch = dt ? resolveByYear(dt.pitch, year) : null;
    var dtLinks = dt ? resolveByYear(dt.links, year) : null;
    var dtFront = dt ? resolveByYear(dt.front_teeth, year) : null;
    var dtRear = dt ? resolveByYear(dt.rear_teeth, year) : null;
    var dtSlack = dt ? resolveByYear(dt.slack, year) : null;
    var dtRows = [
      { label: 'Chain pitch', value: dtPitch ? esc(String(dtPitch)) : null },
      { label: 'Chain length', value: dtLinks ? (dtLinks + ' links') : null },
      { label: 'Front sprocket', value: dtFront ? (dtFront + 'T') : null },
      { label: 'Rear sprocket', value: dtRear ? (dtRear + 'T') : null }
    ];
    // Chain slack (manual-printed, often a range) — row appears once researched.
    if (dtSlack != null) {
      dtRows.push({ label: 'Chain slack', value: esc(String(dtSlack)) + ' mm' });
    }
    sections.push({ key: 'drivetrain', title: 'Chain & sprockets', icon: torqueIcon(), rows: dtRows,
      link: links.drivetrain, linkText: 'Shop chain & sprockets for this bike' });

    // ── Brakes ── (always shown; missing rows say "we don't have this spec yet")
    var cal = spec && spec.caliper;
    var calRows = [
      { label: 'Front caliper', value: (cal && cal.front)
          ? esc(cal.front.make + (cal.front.model ? ' (' + cal.front.model + ')' : '')) : null },
      { label: 'Rear caliper', value: (cal && cal.rear)
          ? esc(cal.rear.make + (cal.rear.model ? ' (' + cal.rear.model + ')' : '')) : null }
    ];
    // Brake pad PNs (EBC FA / OEM) — rows appear once researched, gen-aware via by_year.
    var pads = spec && spec.pads;
    var padF = (pads && pads.f) ? resolveByYear(pads.f, year) : null;
    var padR = (pads && pads.r) ? resolveByYear(pads.r, year) : null;
    if (padF != null || padR != null) {
      calRows.push({ label: 'Front brake pads', value: padF != null ? esc(padF) : null });
      calRows.push({ label: 'Rear brake pads', value: padR != null ? esc(padR) : null });
    }
    // Brake fluid spec (DOT class, manual-printed) — row appears once researched.
    var bfluid = (spec && spec.brake_fluid) ? resolveByYear(spec.brake_fluid, year) : null;
    if (bfluid != null) {
      calRows.push({ label: 'Brake fluid', value: esc(String(bfluid)) });
    }
    sections.push({ key: 'caliper', title: 'Brakes', icon: torqueIcon(), rows: calRows });

    // ── Battery ──
    var bat = spec && spec.battery;
    var batRows = [];
    if (bat) {
      /* by_year FIRST (RCA 2026-07-28): a flat `model` alongside by_year segments must
         never short-circuit the year-accurate answer — that is how gen-segmented fills
         displayed the newest value for every year. Flat model is the fallback ONLY when
         no segments exist. */
      var batModel = bat.by_year ? resolveByYear(bat, year) : (bat.model || null);
      batRows.push({ label: 'Battery', value: batModel ? esc(batModel) : null });
      var volts = bat.v || (bat.spec && bat.spec.voltage_v);
      batRows.push({ label: 'Voltage', value: volts ? (volts + 'V') : null });
      if (bat.spec) {
        var s = bat.spec;
        var extra = [];
        if (s.capacity_ah != null) extra.push(s.capacity_ah + ' Ah');
        if (s.cca != null) extra.push(s.cca + ' CCA');
        if (s.chemistry) extra.push(esc(s.chemistry));
        if (extra.length) batRows.push({ label: 'Battery spec', value: extra.join(' · ') });
      }
    } else {
      batRows.push({ label: 'Battery', value: null });
    }
    sections.push({ key: 'battery', title: 'Battery', icon: batteryIcon(), rows: batRows,
      link: links.battery, linkText: 'Shop batteries for this bike' });

    // ── Spark plug ──
    var spark = spec && spec.spark;
    var sparkRows = [];
    if (spark) {
      var plug = (spark.by_year ? resolveByYear({ by_year: spark.by_year, v: spark.plug }, year) : spark.plug);
      sparkRows.push({ label: 'Spark plug', value: plug ? esc(plug) : null });
      // gap rides the plug, so it is year-resolved the same way — a flat gap showed the
      // newest generation's figure to every older year
      var gap = (spark.gap_by_year ? resolveByYear({ by_year: spark.gap_by_year, v: spark.gap }, year) : spark.gap);
      sparkRows.push({ label: 'Gap', value: (gap != null) ? (gap + ' mm') : null });
    } else {
      sparkRows.push({ label: 'Spark plug', value: null });
    }
    // NO shop link yet: the NGK plug products aren't findable via the YMM filter (a labeling
    // issue) and plug fitment isn't wired onto them, so a "shop plugs" button would dead-end to
    // an empty grid. Add link: links.spark once the plug findability + fitment wiring lands.
    sections.push({ key: 'spark', title: 'Spark plug', icon: sparkIcon(), rows: sparkRows });

    // ── Valve clearance ── (4T service spec; section appears once researched. Values are
    // as-printed cold-clearance figures, often ranges like "0.10-0.15".)
    var vc = spec && spec.vc;
    var vcIn = (vc && vc['in']) ? resolveByYear(vc['in'], year) : null;
    var vcEx = (vc && vc.ex) ? resolveByYear(vc.ex, year) : null;
    if (vcIn != null || vcEx != null) {
      sections.push({ key: 'valves', title: 'Valve clearance', icon: torqueIcon(), rows: [
        { label: 'Valve clearance (intake)', value: vcIn != null ? esc(String(vcIn)) + ' mm' : null },
        { label: 'Valve clearance (exhaust)', value: vcEx != null ? esc(String(vcEx)) + ' mm' : null }
      ]});
    }

    // ── Fuel ── (tank + reserve, manual-printed; section appears once researched)
    var fuel = spec && spec.fuel;
    if (fuel && (fuel.cap || fuel.res)) {
      sections.push({ key: 'fuel', title: 'Fuel capacity', icon: oilIcon(), rows: [
        { label: 'Fuel capacity', value: fuel.cap ? fmtCap(fuel.cap, year) : null },
        { label: 'Fuel reserve', value: fuel.res ? fmtCap(fuel.res, year) : null }
      ]});
    }

    // ── Fork ── (always shown; missing rows say "we don't have this spec yet")
    var fork = spec && spec.fork;
    var ftype = fork ? resolveByYear(fork.type, year) : null;
    var tube = fork ? resolveByYear(fork.tube_dia_mm, year) : null;
    var seal = fork ? resolveByYear(fork.seal_dim, year) : null;
    var fmake = fork ? resolveByYear(fork.make, year) : null;
    var foWt = fork ? resolveByYear(fork.oil_weight_wt, year) : null;
    var foCap = fork ? resolveByYear(fork.oil_capacity_ml, year) : null;
    var foLvl = fork ? resolveByYear(fork.oil_level_mm, year) : null;
    var forkRows = [
      { label: 'Fork type', value: ftype != null ? titleCase(ftype) : null },
      { label: 'Fork tube diameter', value: tube != null ? (tube + ' mm') : null },
      { label: 'Fork seal size', value: seal != null ? (esc(seal) + ' mm') : null },
      { label: 'Fork make', value: fmake != null ? titleCase(fmake) : null }
    ];
    // fork-oil service specs (projected for ~930 bikes, previously hidden) — show when present
    if (foWt != null) forkRows.push({ label: 'Fork oil weight', value: esc(foWt) + ' wt' });
    if (foCap != null) forkRows.push({ label: 'Fork oil capacity (per leg)', value: esc(foCap) + ' ml' });
    if (foLvl != null) forkRows.push({ label: 'Fork oil level', value: esc(foLvl) + ' mm' });
    sections.push({ key: 'fork', title: 'Front fork', icon: forkIcon(), rows: forkRows,
      link: links.fork, linkText: 'Shop fork & suspension for this bike' });

    // ── Starter ── (always shown; missing says "we don't have this spec yet")
    sections.push({ key: 'start', title: 'Starting', icon: starterIcon(),
      rows: [{ label: 'Starter type', value: (spec && spec.start_type) ? titleCase(spec.start_type) : null }] });

    return sections;
  }

  function titleCase(s) {
    s = String(s == null ? '' : s);
    return s.replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  // ─── deep-links to the matching parts ────────────────────────────────────
  // The collection finders read localStorage.gara_ymm (which we set on select),
  // so linking to a collection lands the rider with their bike preselected.
  // Strict 2-stroke model detector. Only consulted when the spine has NO 4-stroke
  // viscosity for the bike (double-gate in buildLinks), so a false positive can only
  // affect an already-unspecced bike. The trailing \b / negative lookahead cleanly
  // exclude the 4-stroke -F variants (CRF/YZF/RM-Z/KXF, SX-F/EXC-F/FC/FE/MC-F).
  function isTwoStroke(ymm) {
    var s = (ymm.make + ' ' + ymm.model);
    // Japanese MX/mini 2-strokes: CR/YZ/RM/KX + a 2-stroke displacement, NOT the -F 4T
    // (the trailing \b drops YZ250F/KX250F; CRF/YZF/RMZ fail because a letter follows).
    if (/\b(?:cr|yz|rm|kx)\s?-?(?:60|65|80|85|100|125|250|360|500)\b/i.test(s)) return true;
    if (/\b(?:kdx|rmx)\s?\d/i.test(s)) return true;                       // Kawasaki KDX, Suzuki RMX (2T)
    // KTM 2-stroke: 125/150/200/250/300 SX/XC/EXC/MXC, never the -F 4-strokes.
    if (/\b(?:125|150|200|250|300)\s?-?(?:sx|xc|exc|mxc)(?!\s?-?f)\b/i.test(s)) return true;
    // Husqvarna 2-stroke TC/TE (FC/FE are 4T); GasGas 2-stroke MC/EC (MC-F/EX-F are 4T).
    if (/\b(?:tc|te)\s?-?(?:125|150|250|300)\b/i.test(s)) return true;
    if (/\b(?:mc|ec)\s?-?(?:125|250|300)(?!\s?-?f)\b/i.test(s)) return true;
    return false;
  }

  // ─── per-bike fitment-page handle (ONE page per model) ───────────────────
  // Every model has exactly ONE fitment page (parts-for-<make>-<model>); it is generation-aware
  // on the page and focuses the rider's year via a ?year= param (see buildLinks). Base on the
  // CANONICAL registry slug (the identity layer's output, e.g. suzuki/dr-650-se), NOT
  // slugify(make)-slugify(model) — that mismatch is what made the link "grain-fragile" before.
  // Returns '' when the bike has no live page (caller falls back to the shop mega-nav).
  function fitmentHandle(ymm) {
    if (!ymm) return '';
    var raw = ymm.slug ? String(ymm.slug) : (slugify(ymm.make) + '/' + slugify(ymm.model));
    var base = handleize('parts-for-' + raw.replace(/\//g, '-'));
    return fitSet().has(base) ? base : '';
  }

  function buildLinks(ymm, spec) {
    // allParts -> the SERVER-RENDERED per-bike fitment page (parts-for-<make>-<model>
    // [-<gen-years>] for generation-split bikes), which lists ONLY the specific-fit
    // parts for this exact bike + generation. This replaces the old /collections/all
    // target, which forced a heavy client-side filter across the whole catalog and
    // paginated into empty pages. The page handle is keyed on the SAME canonical slug
    // the product index + dropdown use (slugify === index_builder._slug); the
    // fitment-page builder creates a page for every bike (+ generation) with >=1 part.
    // Collection finders still back the oil/tire section links.
    var slug = slugify(ymm.make) + '-' + slugify(ymm.model);
    // Pass the bike's manufacturer-spec oil weight so the maintenance collection
    // shows only that viscosity of engine oil (gara-oil-visc-filter.js reads ?oilv),
    // instead of the whole shelf of weights. Honest to the spine; omitted when unknown.
    // resolveByYear, NOT .v — a gen-gated viscosity (multi-gen bike) has v=null with the
    // real values in by_year; reading .v alone dropped ?oilv for those bikes (2015 MT-09).
    var qyear = Number(ymm.year) || 0;
    var visc = spec && spec.oil && spec.oil.visc
      ? resolveByYear(spec.oil.visc, qyear) : null;
    // The spine's own stroke label, and the bike's gearbox weight for the selected year.
    // oil.stroke is a BARE STRING in the projection ("2T"|"4T"|"electric"), not a datum.
    var oilStroke = spec && spec.oil ? spec.oil.stroke : null;
    var gearVisc = spec && spec.oil && spec.oil.gearbox
      ? resolveByYear(spec.oil.gearbox.visc, qyear) : null;
    // Land on the combined Oil & Filters collection (engine oil OR oil filter, ~181
    // items on one page), NOT the 1,100+ item maintenance grid. There the YMM filter
    // narrows the oil FILTERS to this bike, and ?oilv narrows the ENGINE OILS to the
    // bike's weight (gara-oil-visc-filter.js), so the rider sees their oil + filters.
    var oilUrl = '/collections/moto-oil-and-filters';
    // STROKE FIRST. The spine's classification decides the view, never a leftover viscosity:
    // a 2-stroke that still carries a stray 4-stroke weight used to fall into the ?oilv
    // branch and be sold engine oil it has no sump for. The name regex below stays ONLY as
    // the fallback for a bike the classification has not reached.
    if (oilStroke === '2T') {
      oilUrl += '?oilstroke=2t';
      // ...and the bike's own gearbox weight, so the 2-stroke view can also show the
      // transmission-oil bottles it actually takes. Omitted when the spine holds none.
      if (gearVisc) oilUrl += '&oilgb=' + encodeURIComponent(gearVisc);
    } else if (visc) {
      oilUrl += '?oilv=' + encodeURIComponent(visc);
    } else if (isTwoStroke(ymm)) {
      // Unclassified bike with no 4-stroke multigrade on the spine, and the model name is
      // an unambiguous 2-stroke -> show ONLY 2-stroke / pre-mix oils, not the 4T shelf.
      oilUrl += '?oilstroke=2t';
    }
    // "see all parts that fit your bike" -> the SERVER-RENDERED per-bike fitment page when one
    // exists (richer than a category-filtered collection: verified fitment + JSON-LD + FAQ). For
    // bikes with no page (~21%, too few parts), fall back to opening the shop mega-nav with the
    // bike already loaded. fitmentHandle resolves through the canonical slug + the live page set,
    // so it lands on the gen-correct page and never on a 404.
    // Per-bike parts pages RETIRED 2026-06-08 (Benjamin): the "see every part" CTA now ALWAYS
    // opens the shop mega-nav, with the bike saved site-wide (GaraYMM.set fires on click below),
    // so the rider picks a category and that collection lands pre-filtered to their bike. No
    // per-bike parts page is in the path anymore. (/collections/all is only the no-JS fallback.)
    // Battery / chain & sprockets / fork land on their category collection with the
    // bike carried site-wide (GaraYMM.set fires on click below), so the collection's
    // YMM filter narrows to the parts that fit this exact bike. Same mechanism as the
    // oil/tire links — the spine-spec-match narrowing (battery-by-model, chain-by-pitch)
    // is a separate, additive layer; even without it these land on a real filtered grid.
    return {
      oil: oilUrl,
      // Coolant lives in its own maintenance collection — do NOT reuse the oil
      // link (the Oil & Filters grid has no coolant, so "Shop coolant" used to
      // dead-end on engine oil). The collection's YMM filter still narrows to
      // this bike via the GaraYMM.set fired on click below.
      coolant: '/collections/moto-maintenance-coolant',
      tire: '/collections/moto-tires',
      battery: '/collections/moto-electrical-battery',
      drivetrain: '/collections/moto-drivetrain',
      fork: '/collections/moto-suspension',
      allParts: '/collections/all',
      shopOpen: 'onclick="return window.garaOpenShop ? garaOpenShop(event) : true;"'
    };
  }

  // ─── render one QMS card grid for a bike ─────────────────────────────────
  function renderQMS(container, ymm) {
    var slug = bikeSlug(ymm);
    var spec = specFor(slug);
    var links = buildLinks(ymm, spec);
    var sections = buildSections(spec, ymm, links);

    var yrLabel = ymm.year ? (ymm.year + ' ') : '';
    var html = '';
    html += '<div class="qms">';
    html += '<div class="qms__head">';
    html += '<h3 class="qms__bike">' + esc(yrLabel + ymm.make + ' ' + ymm.model) + '</h3>';
    if (!spec) {
      html += '<p class="qms__nospec">We don\'t have a full spec sheet for this bike yet. ' +
        'We\'re adding bikes all the time. In the meantime you can still ' +
        '<a href="' + links.allParts + '" ' + links.shopOpen + '>see the parts that fit it</a>.</p>';
    } else {
      html += '<p class="qms__sub">Your bike\'s maintenance numbers, pulled from the manufacturer specs. ' +
        'Every section links to the parts that fit.</p>';
    }
    html += '</div>';

    html += '<div class="qms__grid">';
    sections.forEach(function (sec) {
      html += '<div class="qms__card" data-qms-sec="' + sec.key + '">';
      html += '<div class="qms__card-h">' + sec.icon + '<span>' + esc(sec.title) + '</span></div>';
      html += '<dl class="qms__rows">';
      sec.rows.forEach(function (r) {
        html += '<div class="qms__row">';
        html += '<dt>' + esc(r.label) + '</dt>';
        html += '<dd>' + (r.value == null ? MISSING : r.value) + '</dd>';
        html += '</div>';
      });
      html += '</dl>';
      if (sec.link) {
        html += '<a class="qms__shop" href="' + sec.link + '">' + esc(sec.linkText || 'Shop parts') + ' &rarr;</a>';
      }
      html += '</div>';
    });
    html += '</div>';   // grid

    html += '<div class="qms__foot">';
    html += '<a class="qms__allparts" href="' + links.allParts + '" ' + links.shopOpen + '>See every part that fits your ' +
      esc(ymm.make + ' ' + ymm.model) + ' &rarr;</a>';
    html += '</div>';
    html += '</div>';   // qms

    container.innerHTML = html;

    // Every shop link on THIS sheet must carry THIS sheet's bike site-wide, so
    // the destination collection/finder lands on the exact bike whose sheet you
    // clicked, not whatever YMM was last set on some other page (the stale-bike
    // bug: viewing the RC51 sheet but the tire page opened on the MT-09). Write
    // synchronously on click; localStorage is flushed before the navigation, so
    // the tire finder's restoreYMM() reads the right bike on the next page. This
    // also re-resolves the bike's stock front/rear sizes (GaraYMM.set), which is
    // exactly what the tire grouper's both-ends (#1006) rule matches against.
    // The bike's tire sizes live in the SPINE (spec.tire), already resolved on
    // THIS sheet (by_year / generation aware). Carry them through so the tire
    // grouper matches the bike's front+rear directly — never re-derived from a
    // parallel bike-size list (GARA_BIKE_SIZES). That drift is exactly what
    // silently showed non-pair families. Spine is the single source.
    var sized = ymm;
    if (spec && spec.tire) {
      var _y = Number(ymm.year) || 0;
      var _f = resolveByYear(spec.tire.f, _y);
      var _r = resolveByYear(spec.tire.r, _y);
      if (_f || _r) {
        sized = {};
        for (var _k in ymm) { if (Object.prototype.hasOwnProperty.call(ymm, _k)) sized[_k] = ymm[_k]; }
        if (_f) sized.front = _f;
        if (_r) sized.rear = _r;
        // Carry the OE service rating too, so the tire finder/picker can warn
        // when a tire is below the bike's required load/speed.
        var _fr = spec.tire.f_rating ? resolveByYear(spec.tire.f_rating, _y) : null;
        var _rr = spec.tire.r_rating ? resolveByYear(spec.tire.r_rating, _y) : null;
        if (_fr) sized.front_rating = _fr;
        if (_rr) sized.rear_rating = _rr;
      }
    }
    var shopLinks = container.querySelectorAll('.qms__shop, .qms__allparts');
    for (var li = 0; li < shopLinks.length; li++) {
      shopLinks[li].addEventListener('click', function () {
        try { if (global.GaraYMM && global.GaraYMM.set) global.GaraYMM.set(sized); } catch (_) {}
      });
    }
  }

  // ─── garage persistence ──────────────────────────────────────────────────
  var GARAGE_KEY = 'gara_garage';

  function readGarage() {
    try {
      var raw = global.localStorage.getItem(GARAGE_KEY);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch (_) { return []; }
  }
  function writeGarage(arr) {
    try { global.localStorage.setItem(GARAGE_KEY, JSON.stringify(arr || [])); } catch (_) {}
  }
  function bikeKey(b) { return bikeSlug(b) + '@' + (b.year || ''); }
  function addToGarage(ymm) {
    var arr = readGarage();
    var entry = { slug: bikeSlug(ymm), make: ymm.make, model: ymm.model, year: ymm.year };
    var k = bikeKey(entry);
    if (!arr.some(function (b) { return bikeKey(b) === k; })) {
      arr.push(entry);
      writeGarage(arr);
      maybePersistMetafield(arr);
    }
    return arr;
  }
  function removeFromGarage(key) {
    var arr = readGarage().filter(function (b) { return bikeKey(b) !== key; });
    writeGarage(arr);
    maybePersistMetafield(arr);
    return arr;
  }

  // Phase-2 hook: if the page is logged-in AND the lead has wired the app-proxy
  // write endpoint, POST the garage to persist it on the customer metafield.
  // Until then this is a no-op (localStorage is authoritative). The endpoint
  // path is read from a data attribute so the lead can set it without a JS edit.
  function maybePersistMetafield(arr) {
    var root = global.document.querySelector('[data-gara-garage-backend="metafield"]');
    if (!root) return;
    var url = root.getAttribute('data-gara-garage-endpoint');
    if (!url) return;   // endpoint not wired yet
    try {
      global.fetch(url, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ garage: arr })
      }).catch(function () {});
    } catch (_) {}
  }

  // Merge any server-rendered (metafield) bikes into the localStorage garage so
  // a logged-in rider sees their saved bikes even before the write-path exists.
  function mergeSeedGarage(seed) {
    if (!Array.isArray(seed) || !seed.length) return readGarage();
    var arr = readGarage();
    var seen = {};
    arr.forEach(function (b) { seen[bikeKey(b)] = true; });
    seed.forEach(function (b) {
      if (!b || !b.make || !b.model) return;
      var entry = { slug: b.slug || bikeSlug(b), make: b.make, model: b.model, year: b.year };
      var k = bikeKey(entry);
      if (!seen[k]) { arr.push(entry); seen[k] = true; }
    });
    writeGarage(arr);
    return arr;
  }

  // ─── picker UI ───────────────────────────────────────────────────────────
  function buildPicker(host, onSelect) {
    var bikes = registry();
    if (!bikes.length) {
      host.innerHTML = '<p class="qms-picker__err">The bike list is still loading. Refresh the page and try again.</p>';
      return null;
    }

    host.innerHTML =
      '<div class="qms-picker__row">' +
      '  <select class="qms-picker__sel" data-qp-year aria-label="Year"><option value="">Year</option></select>' +
      '  <select class="qms-picker__sel" data-qp-make aria-label="Make" disabled><option value="">Make</option></select>' +
      '  <select class="qms-picker__sel" data-qp-model aria-label="Model" disabled><option value="">Model</option></select>' +
      '</div>';

    var yearSel = host.querySelector('[data-qp-year]');
    var makeSel = host.querySelector('[data-qp-make]');
    var modelSel = host.querySelector('[data-qp-model]');

    // Year list (guard garbage years like the parts finder does).
    var years = {};
    bikes.forEach(function (b) {
      var y1 = b.year_start, y2 = b.year_end || y1;
      if (!y1 || y1 < 1950 || y1 > 2030) return;
      if (!y2 || y2 < y1 || y2 > 2030) y2 = y1;
      for (var y = y1; y <= y2; y++) years[y] = true;
    });
    Object.keys(years).map(Number).sort(function (a, b) { return b - a; }).forEach(function (y) {
      var o = global.document.createElement('option'); o.value = y; o.textContent = y;
      yearSel.appendChild(o);
    });

    function clearSel(sel, ph) {
      while (sel.options.length > 1) sel.remove(1);
      sel.options[0].textContent = ph; sel.value = ''; sel.disabled = true;
    }
    function bikesForYear(y) {
      y = Number(y);
      return bikes.filter(function (b) {
        var y1 = b.year_start || 0, y2 = b.year_end || y1;
        return y >= y1 && y <= y2;
      });
    }

    yearSel.addEventListener('change', function () {
      clearSel(makeSel, 'Make'); clearSel(modelSel, 'Model');
      if (!yearSel.value) return;
      var makes = {};
      bikesForYear(yearSel.value).forEach(function (b) { if (b.make) makes[b.make] = true; });
      Object.keys(makes).sort().forEach(function (m) {
        var o = global.document.createElement('option'); o.value = m; o.textContent = m; makeSel.appendChild(o);
      });
      makeSel.disabled = false;
    });
    makeSel.addEventListener('change', function () {
      clearSel(modelSel, 'Model');
      if (!makeSel.value) return;
      var models = {};
      bikesForYear(yearSel.value).forEach(function (b) {
        if (b.make === makeSel.value && b.model) models[b.model] = true;
      });
      Object.keys(models).sort().forEach(function (m) {
        var o = global.document.createElement('option'); o.value = m; o.textContent = m; modelSel.appendChild(o);
      });
      modelSel.disabled = false;
    });
    modelSel.addEventListener('change', function () {
      if (!modelSel.value) return;
      var y = Number(yearSel.value);
      var bike = bikes.find(function (b) {
        var y1 = b.year_start || 0, y2 = b.year_end || y1;
        return y >= y1 && y <= y2 && b.make === makeSel.value && b.model === modelSel.value;
      });
      var ymm = { year: y, make: makeSel.value, model: modelSel.value };
      if (bike && bike.slug) ymm.slug = bike.slug;
      // Sync site-wide YMM so the rider's bike also filters every collection.
      try { if (global.GaraYMM && global.GaraYMM.set) global.GaraYMM.set(ymm); } catch (_) {}
      onSelect(ymm);
    });

    return {
      reset: function () {
        yearSel.value = ''; clearSel(makeSel, 'Make'); clearSel(modelSel, 'Model');
      }
    };
  }

  // ─── icons (inline SVG, currentColor) ────────────────────────────────────
  function svg(p) { return '<svg viewBox="0 0 24 24" class="qms-ic" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' + p + '</svg>'; }
  // Oil: a single oil droplet (universal fluid symbol).
  function oilIcon()    { return svg('<path d="M12 3.5c0 0 5 5.6 5 9.2a5 5 0 0 1-10 0c0-3.6 5-9.2 5-9.2z"/>'); }
  function tireIcon()   { return svg('<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3"/>'); }
  function torqueIcon() { return svg('<path d="M14 5a3.5 3.5 0 0 0-4 5l-5 5 2 2 5-5a3.5 3.5 0 0 0 5-4l-2 2-2-1-1-2z"/>'); }
  function batteryIcon(){ return svg('<rect x="3" y="8" width="16" height="9" rx="1.5"/><path d="M19 11h2v3h-2M7 6v2M14 6v2M9 12h4M11 10v4"/>'); }
  // Spark plug: terminal nut on top, insulator body, threaded shell, electrode tip.
  function sparkIcon()  { return svg('<path d="M10 2.5h4v2.5h-4z"/><path d="M9.5 5h5v3.5l-1 2.5h-3l-1-2.5z"/><path d="M10 12h4M10.3 14h3.4"/><path d="M12 11v6"/>'); }
  // Front fork: triple-clamp bar, two fork tubes, and the front wheel.
  function forkIcon()   { return svg('<path d="M8 3h8"/><path d="M9 3.5v7.5M15 3.5v7.5"/><circle cx="12" cy="16" r="4.2"/><path d="M9 11l3 1.5 3-1.5"/>'); }
  function starterIcon(){ return svg('<path d="M13 2 4 14h7l-1 8 9-12h-7z"/>'); }

  // ─── export ──────────────────────────────────────────────────────────────
  global.GaraQMS = {
    slugify: slugify,
    bikeSlug: bikeSlug,
    specFor: specFor,
    resolveByYear: resolveByYear,
    buildPicker: buildPicker,
    renderQMS: renderQMS,
    readGarage: readGarage,
    addToGarage: addToGarage,
    removeFromGarage: removeFromGarage,
    mergeSeedGarage: mergeSeedGarage,
    bikeKey: bikeKey
  };
})(typeof window !== 'undefined' ? window : this);
